/**
 * Main viewing area of Explorer.
 * This hosts the map, timeline, and others.
 */
import {Box} from '@mui/material';
import {blueGrey} from '@mui/material/colors';
import {bool, func, object, string} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';

import CompareSlider from '../compare/compare-slider.jsx';
import CompareTools from '../compare/compare-tools.jsx';
import MapControls from './map/controls.jsx';
import Map from './map/index.jsx';
import CompareMode from '@explorer/components/compare';
import {MeasureOverlay} from '@explorer/components/map/measure/measure-overlay';
import Legend from '@explorer/components/map/process-color-picker';
import Snapshot from '@explorer/components/snapshot/snapshot';
import StatusBar from '@explorer/components/statusbar/status-bar';
import Timeline from '@explorer/components/timeline';
import {COMPARE_METHODS} from '@explorer/constants';
import dimensions, {
  LEFT_DRAWER_WIDTH,
  RIGHT_DRAWER_WIDTH,
} from '@explorer/styles/dimensions.js';

const COMPARE_MODE_VALUES = {
  [COMPARE_METHODS.SLIDER]: 0.5,
  [COMPARE_METHODS.FADE]: 0,
  [COMPARE_METHODS.DIFF]: [0.2, 1],
};

const Viewer = ({
  showTimeline,
  leftDrawer,
  rightDrawer,
  isComparing,
  ...props
}) => {
  const [tempGeometry, setTempGeometry] = useState(null);
  const [measureFeatures, setMeasureFeatures] = useState([]);
  const [searchFeatures, setSearchFeatures] = useState([]);
  const [hoverFeatures, setHoverFeatures] = useState([]);
  const [compareModeValues, setCompareModeValues] =
    useState(COMPARE_MODE_VALUES);

  const drawTool = useSelector(state => state.map.drawTool);

  const [mapRef, setMapRef] = useState();

  // we want snaphot lab to re-render on ref existing
  // https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  // there might be better ways, this works for now
  const mapRefChanged = useCallback(node => {
    if (node !== null) {
      setMapRef(node);
    }
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: blueGrey[800],
      }}
    >
      {/*
       * The map needs to be positioned _below_ the layout so that the
       * center does not shift unexpectedly for the user.
       */}
      <Map
        setTempGeometry={setTempGeometry}
        measureFeatures={measureFeatures}
        setMeasureFeatures={setMeasureFeatures}
        searchFeatures={searchFeatures}
        setSearchFeatures={setSearchFeatures}
        mapProps={{ref: mapRefChanged}}
        compareModeValues={compareModeValues}
        hoverFeatures={hoverFeatures}
      />
      {/*
       * Drawers are managed in a different part of the DOM tree,
       * the "viewer" is meant to be all the bits of the UI in the middle.
       * Here, when a draw is opened, it will shift the layout to account
       * for contents, the only catch is to manage the pointer-events.
       * Any component requiring the pointer-events needs to have them
       * re-enabled.
       */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          height: '100%',
          left: leftDrawer ? LEFT_DRAWER_WIDTH : 0,
          right: rightDrawer ? RIGHT_DRAWER_WIDTH : 0,
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
          }}
        >
          <Box
            sx={{
              flex: 1,
            }}
          >
            {/* This should be left empty to account for events falling through to the map. */}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'end',
            }}
          >
            <Box
              sx={theme => ({
                marginBottom: theme.spacing(1),
                marginRight: theme.spacing(1),
                pointerEvents: 'all',
              })}
            >
              <MapControls
                tempGeometry={tempGeometry}
                setTempGeometry={setTempGeometry}
                measureFeatures={measureFeatures}
                setMeasureFeatures={setMeasureFeatures}
                searchFeatures={searchFeatures}
                setSearchFeatures={setSearchFeatures}
                setHoverFeatures={setHoverFeatures}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{pointerEvents: 'all'}}>
          {showTimeline && <Timeline />}
          {isComparing && (
            <CompareTools
              onFadeChange={value =>
                setCompareModeValues({
                  ...compareModeValues,
                  [COMPARE_METHODS.FADE]: value,
                })
              }
              fadeValue={compareModeValues[COMPARE_METHODS.FADE]}
              onPixelDiffChange={values =>
                setCompareModeValues({
                  ...compareModeValues,
                  [COMPARE_METHODS.DIFF]: values,
                })
              }
              pixelDiffRangeValue={compareModeValues[COMPARE_METHODS.DIFF]}
            />
          )}
          {mapRef?.addControl && (
            <StatusBar addMapControl={control => mapRef.addControl(control)} />
          )}
        </Box>
      </Box>
      <>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            height: theme =>
              `calc(100% - ${dimensions.statusbar.height}px - ${theme.spacing(
                6
              )})`,
            left: leftDrawer ? `${LEFT_DRAWER_WIDTH}px` : 0,
            right: rightDrawer ? `${RIGHT_DRAWER_WIDTH}px` : 0,
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'none',
          }}
        >
          <Box sx={{pointerEvents: 'all'}}>
            {mapRef && <Snapshot map={mapRef} />}
          </Box>
          {isComparing && <CompareMode />}
        </Box>

        {isComparing && props.compareMethod === COMPARE_METHODS.SLIDER && (
          <CompareSlider
            mapWidth={mapRef?.getSize()[0]}
            onChange={value =>
              setCompareModeValues({
                ...compareModeValues,
                [COMPARE_METHODS.SLIDER]: value,
              })
            }
            position={compareModeValues[COMPARE_METHODS.SLIDER]}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            bottom: theme =>
              `calc((${
                showTimeline ? `${dimensions.timeline.height}px` : '0px'
              }) + ${theme.spacing(4)})`,
            left: theme =>
              `calc(${leftDrawer ? LEFT_DRAWER_WIDTH : 0}px +  ${theme.spacing(
                2
              )})`,
            right: theme =>
              rightDrawer ? `${RIGHT_DRAWER_WIDTH}px` : theme.spacing(2),
            pointerEvents: 'none',
            maxWidth: '400px',
          }}
        >
          <Box sx={{pointerEvents: 'all'}}>
            <Legend />
          </Box>
        </Box>
      </>
      {measureFeatures
        .filter(
          f => f.properties?.visible !== false && drawTool.target !== f.id
        )
        .map(mFeature => {
          return (
            <MeasureOverlay
              key={mFeature.id}
              geometry={mFeature.geometry}
              mapRef={mapRef}
            />
          );
        })}
    </Box>
  );
};

Viewer.propTypes = {
  showTimeline: bool,
  leftDrawer: string,
  rightDrawer: string,
  onSearchHere: func,
  isComparing: bool,
  compareMethod: string,
  showSnapshotLab: bool,
  drawTool: object,
};

export default Viewer;
